import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'
import { requestFailed, requestFinished, requestStarted, setMessage } from '../common/common.actions'
import i18n from 'i18next'
import { EventType, Severity } from '@professocial/professocialTypes/types'
import {
  DeleteMediaFileRequest, GetMediaFileMetadataRequest,
  ListMediaFilesRequest, SetMediaUsageRequest, UploadMediaFileRequest, UploadTemporaryMediaFileRequest,
} from '@3m5/sm_publish-frontend-api'
import {
  checkFileUpload,
  checkFileUploadInitiated,
  deleteMediaFile,
  deleteMediaFileInitiated,
  getMediaFileMetadata,
  getMediaFileMetadataInitiated, getUploadedFileAsBase64, getUploadedFileAsBase64Initiated,
  listCoverImages,
  listCoverImagesInitiated,
  listMediaFiles,
  listMediaFilesInitiated,
  listProfileImages,
  listProfileImagesInitiated,
  setMediaFileUsage, setMediaFileUsageInitiated,
  uploadMediaFile,
  uploadMediaFileInitiated,
  uploadTemporaryMediaFile,
  uploadTemporaryMediaFileInitiated, uploadThumbnailForMediaFile, uploadThumbnailForMediaFileInitiated,
} from './images.actions'
import { FileScannerApi, MediaFileApi } from '../../api/instances'
import { CheckFileUploadRequest, GetUploadedFileAsBase64Request } from '../../api/filescanner/types'
import { UploadThumbnailForMediaFileRequest } from '@3m5/sm_publish-frontend-api/apis/MediaFileApi'

// getMediaFileMetadata
const getMediaFileMetadataWorker = bindAsyncAction(getMediaFileMetadata)(
  function * (params: GetMediaFileMetadataRequest): any {
    return yield call([MediaFileApi, MediaFileApi.getMediaFileMetadata], params)
  },
)

function * watchGetMediaFileMetadataInitiated () {
  while (true) {
    const action: Action<GetMediaFileMetadataRequest> = yield take(getMediaFileMetadataInitiated)
    try {
      yield getMediaFileMetadataWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:getMediaFileMetadataError',
        eventType: EventType.update,
      }))
      console.error('error in watchGetMediaFileMetadataInitiated: ', e)
    }
  }
}

// uploadMediaFile
const uploadMediaFileWorker = bindAsyncAction(uploadMediaFile)(
  function * (params: UploadMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.uploadMediaFile], params)
  },
)

function * watchUploadMediaFileInitiated () {
  while (true) {
    const action: Action<UploadMediaFileRequest> = yield take(uploadMediaFileInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield uploadMediaFileWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({ type: action.type }))
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:uploadMediaFileError',
        eventType: EventType.update,
      }))
      console.error('error in watchUploadMediaFileInitiated: ', e)
    }
  }
}

// uploadThumbnailForMediaFile
const uploadThumbnailForMediaFileWorker = bindAsyncAction(uploadThumbnailForMediaFile)(
  function * (params: UploadThumbnailForMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.uploadThumbnailForMediaFile], params)
  },
)

function * UploadThumbnailForMediaFileInitiated () {
  while (true) {
    const action: Action<UploadThumbnailForMediaFileRequest> = yield take(uploadThumbnailForMediaFileInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield uploadThumbnailForMediaFileWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({ type: action.type }))
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:uploadMediaFileError',
        eventType: EventType.update,
      }))
      console.error('error in watchUploadThumbnailForMediaFileInitiated: ', e)
    }
  }
}

// uploadTemporaryMediaFile
const uploadTemporaryMediaFileWorker = bindAsyncAction(uploadTemporaryMediaFile)(
  function * (params: UploadTemporaryMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.uploadTemporaryMediaFile], params)
  },
)

function * watchUploadTemporaryMediaFileInitiated () {
  while (true) {
    const action: Action<UploadTemporaryMediaFileRequest> = yield take(uploadTemporaryMediaFileInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield uploadTemporaryMediaFileWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:loadError', { context: i18n.t('commonImage:temporaryMediaFile') }),
          eventType: EventType.load,
        },
      }))
      console.error('error in watchUploadTemporaryMediaFileInitiated: ', e)
    }
  }
}

// listMediaFiles
const listMediaFilesWorker = bindAsyncAction(listMediaFiles)(
  function * (params: ListMediaFilesRequest): any {
    return yield call([MediaFileApi, MediaFileApi.listMediaFiles], params)
  },
)

function * watchListMediaFilesInitiated () {
  while (true) {
    const action: Action<ListMediaFilesRequest> = yield take(listMediaFilesInitiated)
    try {
      yield listMediaFilesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:listMediaFilesError',
        eventType: EventType.update,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// listCoverImages
const listCoverImagesWorker = bindAsyncAction(listCoverImages)(
  function * (): any {
    return yield call([MediaFileApi, MediaFileApi.listCoverImages])
  },
)

function * watchListCoverImagesInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(listCoverImagesInitiated)
    try {
      yield listCoverImagesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:listMediaFilesError',
        eventType: EventType.update,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// listProfileImages
const listProfileImagesWorker = bindAsyncAction(listProfileImages)(
  function * (): any {
    return yield call([MediaFileApi, MediaFileApi.listProfileImages])
  },
)

function * watchListProfileImagesInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(listProfileImagesInitiated)
    try {
      yield listProfileImagesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:listMediaFilesError',
        eventType: EventType.update,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// setMediaUsage
const setMediaFileUsageWorker = bindAsyncAction(setMediaFileUsage)(
  function * (params: SetMediaUsageRequest): any {
    return yield call([MediaFileApi, MediaFileApi.setMediaUsage], params)
  },
)

function * watchSetMediaFileUsageInitiated () {
  while (true) {
    const action: Action<SetMediaUsageRequest> = yield take(setMediaFileUsageInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield setMediaFileUsageWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreMessages:isSuccessfullyLoaded', { context: i18n.t('commonImage:newFile') }),
          eventType: EventType.load,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:loadError', { context: i18n.t('commonImage:newFile') }),
          eventType: EventType.load,
        },
        error: e,
      }))
      console.error('error in setMediaUsageInitiated: ', e)
    }
  }
}

// deleteMediaFile
const deleteMediaFileWorker = bindAsyncAction(deleteMediaFile)(
  function * (params: DeleteMediaFileRequest): any {
    return yield call([MediaFileApi, MediaFileApi.deleteMediaFile], params)
  },
)

function * watchDeleteMediaFileInitiated () {
  while (true) {
    const action: Action<DeleteMediaFileRequest> = yield take(deleteMediaFileInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield deleteMediaFileWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreMessages:isSuccessfullyDeleted', { context: i18n.t('commonImage:file') }),
          eventType: EventType.delete,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:deleteError', { context: i18n.t('commonImage:file') }),
          eventType: EventType.delete,
        },
        error: e,
      }))
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:deleteMediaFileError',
        eventType: EventType.update,
      }))
      console.error('error in watchDeleteMediaFileInitiated: ', e)
    }
  }
}

// checkFileUploadInitiated
const checkFileUploadWorker = bindAsyncAction(checkFileUpload)(
  function * (params: CheckFileUploadRequest): any {
    return yield call([FileScannerApi, FileScannerApi.checkFileUpload], params)
  },
)

function * watchCheckFileUploadInitiated () {
  while (true) {
    const action: Action<CheckFileUploadRequest> = yield take(checkFileUploadInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield checkFileUploadWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
      }))
    } catch (e: any) {
      console.log(e)
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: e.status === 413
            ? i18n.t('commonImage:fileIsTooBig')
            : i18n.t('coreMessages:loadError', { context: i18n.t('commonImage:checkMediaFile') }),
          eventType: EventType.upload,
        },
      }))
      console.error('error in watchCheckFileUploadInitiated: ', e)
    }
  }
}

// getUploadedFileAsBase64
const getUploadedFileAsBase64Worker = bindAsyncAction(getUploadedFileAsBase64)(
  function * (params: GetUploadedFileAsBase64Request): any {
    return yield call([FileScannerApi, FileScannerApi.getUploadedFileAsBase64], params)
  },
)

function * watchGetUploadedFileAsBase64Initiated () {
  while (true) {
    const action: Action<GetUploadedFileAsBase64Request> = yield take(getUploadedFileAsBase64Initiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield getUploadedFileAsBase64Worker(action.payload)
      yield put(requestFinished({
        type: action.type,
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        error: e,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:loadError', { context: i18n.t('commonImage:temporaryMediaFile') }),
          eventType: EventType.upload,
        },
      }))
      console.error('error in watchGetUploadedFileAsBase64Initiated: ', e)
    }
  }
}

export function * imagesSagas () {
  yield fork(watchGetMediaFileMetadataInitiated)
  yield fork(watchUploadMediaFileInitiated)
  yield fork(watchUploadTemporaryMediaFileInitiated)
  yield fork(watchListMediaFilesInitiated)
  yield fork(watchListCoverImagesInitiated)
  yield fork(watchListProfileImagesInitiated)
  yield fork(watchSetMediaFileUsageInitiated)
  yield fork(watchDeleteMediaFileInitiated)
  yield fork(watchCheckFileUploadInitiated)
  yield fork(watchGetUploadedFileAsBase64Initiated)
  yield fork(UploadThumbnailForMediaFileInitiated)
}
